import { useCallback, useEffect } from 'react';

import { head } from 'lodash';
import { useParams } from 'react-router-dom';

import Api from 'core/api';
import useLocalStorage from 'hooks/useLocalStorage';
import useQueryParams from 'hooks/useQueryParams';
import useRedirect from 'hooks/useRedirect';
import useUserRedux from 'hooks/useUserRedux';
import {
  EVENT_K12_USER_REDIRECT,
  EVENTS_EVENT_DETAILS,
  LOGIN_K12,
  ORG_EVENT_SCHEDULER_DISTRICT_DETAILS,
  ORG_EVENT_SCHEDULER_SCHOOL_DETAILS,
  ONBOARDING_ADD_AFFILIATION,
} from 'routes/constants';
import { ensureArray } from 'utils/arrays';
import { ensureObject } from 'utils/objects';
import { addUrlParams } from 'utils/urls';

import {
  COLLEGE_REPS_KEY,
  INDUSTRY_PARTNER_KEY,
} from 'modules/EventSchedulerV2/constants';

const MemberOrNonLoggedUserEventRedirect = () => {
  const { eventId, districtId } = useParams();
  const { redirect } = useRedirect();
  const queryParams = useQueryParams();
  const { isLoggedIn, isOrganizationGuest } = useUserRedux();

  const [guestUserFormValues] = useLocalStorage('guestUserFormValues');

  const schoolId = queryParams.get('schoolId');
  const { crmOrganization } = ensureObject(guestUserFormValues);

  const loggedInUserRedirect = useCallback(async () => {
    if (isOrganizationGuest && crmOrganization) {
      return redirect({
        to: addUrlParams(
          EVENTS_EVENT_DETAILS({
            eventId,
          }),
          { crmOrganization: crmOrganization.id },
        ),
        reason:
          'Redirect users to member events page if they already have an affiliation',
        component:
          'src/modules/EventSchedulerV2/Member/components/DistrictEventsSection/index.js',
      });
    }

    const { data: employmentConnections } =
      await Api.organizations.getEmploymentConnections();
    const { data: eventDetails } =
      await Api.eventScheduler.getMemberEventDetails({
        hashId: eventId,
      });

    const getOrganization = () => {
      const participantTypes = ensureArray(
        eventDetails?.eventDetail?.participantTypes,
      );

      const isCollegeRepEvent =
        ensureArray(participantTypes).includes(COLLEGE_REPS_KEY);
      const isIndustryPartnerEvent =
        ensureArray(participantTypes).includes(INDUSTRY_PARTNER_KEY);

      const heSchoolConnection = ensureArray(employmentConnections).find(
        conn => conn?.organization?.organizationType === 'HESchool',
      );
      const employerConnection = ensureArray(employmentConnections).find(
        conn => conn?.organization?.organizationType === 'Employer',
      );

      if (isCollegeRepEvent && isIndustryPartnerEvent) {
        return ensureObject(head(employmentConnections)?.organization);
      }

      if (isCollegeRepEvent && heSchoolConnection) {
        return ensureObject(heSchoolConnection?.organization);
      }

      if (isIndustryPartnerEvent && employerConnection) {
        return ensureObject(employerConnection?.organization);
      }

      return ensureObject(head(employmentConnections)?.organization);
    };

    const { uuid: orgUuid } = getOrganization();

    if (orgUuid) {
      const redirectArgs = { orgUuid, districtId, params: { eventId } };
      const redirectUrl = schoolId
        ? ORG_EVENT_SCHEDULER_SCHOOL_DETAILS({ ...redirectArgs, schoolId })
        : ORG_EVENT_SCHEDULER_DISTRICT_DETAILS(redirectArgs);

      return redirect({
        to: redirectUrl,
        reason:
          'Redirect users to member events page if they already have an affiliation',
        component:
          'src/modules/EventSchedulerV2/Member/components/DistrictEventsSection/index.js',
      });
    }

    return redirect({
      to: ONBOARDING_ADD_AFFILIATION({
        params: {
          districtId,
          eventId,
          schoolId,
        },
      }),
      reason:
        'Redirect them to member add affiliation page if user does not have an affiliation',
      component:
        'src/modules/EventSchedulerV2/Member/components/MemberOrNonLoggedUserEventRedirect',
    });
  }, [
    crmOrganization,
    districtId,
    eventId,
    isOrganizationGuest,
    redirect,
    schoolId,
  ]);

  const nonLoggedInUserRedirect = useCallback(() => {
    if (districtId) {
      return redirect({
        to: EVENTS_EVENT_DETAILS({ eventId }),
        reason: 'Redirect them to public event details page',
        component:
          'src/modules/EventSchedulerV2/Public/pages/PublicEventPage/index.js',
      });
    }

    localStorage.setItem(
      'loginRedirectURL',
      JSON.stringify({
        to: EVENT_K12_USER_REDIRECT({ eventId }),
        component: 'src/modules/StudentDashboard',
        reason: 'Redirect user to student dashboard',
      }),
    );

    return redirect({
      to: LOGIN_K12,
      reason: 'Redirect k12_user to login page',
      component: 'src/modules/OnboardingAndLogin',
    });
  }, [districtId, eventId, redirect]);

  useEffect(() => {
    if (isLoggedIn) {
      try {
        loggedInUserRedirect();
      } catch (e) {
        console.error(e);
      }
    } else {
      nonLoggedInUserRedirect();
    }
  }, [isLoggedIn, loggedInUserRedirect, nonLoggedInUserRedirect]);

  return null;
};

export default MemberOrNonLoggedUserEventRedirect;
